import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, LogIn, Loader2, CheckCircle2, XCircle, Coffee, Sparkles, Users, User, Wallet as WalletIcon, MoreHorizontal, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API } from '../context/AdminAuthContext';

/**
 * Feb 2026 — DeskTracker (mobile-first)
 *
 * Standalone full-screen "Leave desk / Return to desk" page. Uses
 * the same `/desk/current`, `/desk/leave`, `/desk/return` endpoints
 * as `DeskButton`, but with a UI optimised for a phone in-hand:
 *
 *   • Single large primary action button (color + copy toggles
 *     between LEAVE DESK and RETURN TO DESK).
 *   • Category picker as chunky tap targets (min 56px height).
 *   • On return, Success / Unsuccess render as two big buttons.
 *   • 24/7 access — no duty-hour gate (backend also enforces this).
 *
 * The component is auth-agnostic: pass `authHeaders` + `basePath`
 * (either `/admin` or `/branch`) and it drives the right endpoint.
 */
const CATEGORY_OPTIONS = [
  { key: 'prayer',       label: 'Prayer',       Icon: Sparkles },
  { key: 'lunch',        label: 'Lunch',        Icon: Coffee },
  { key: 'meeting',      label: 'Meeting',      Icon: Users },
  { key: 'personal',     label: 'Personal',     Icon: User },
  { key: 'cash_deposit', label: 'Cash Deposit', Icon: WalletIcon },
  { key: 'other',        label: 'Other',        Icon: MoreHorizontal },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map((c) => [c.key, c.label]));

// Feb 2026 — the backend serialises desk timestamps as ISO strings in
// UTC (ending in `+00:00`). Legacy rows may lack an offset; assume UTC
// in that case so the elapsed timer stays correct across timezones.
const parseUtcIso = (raw) => {
  if (!raw || typeof raw !== 'string') return NaN;
  const s = raw.trim();
  const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasOffset ? s : `${s}Z`).getTime();
};

const _fmtElapsed = (mins) => {
  const m = Math.max(0, Math.floor(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h > 0) return `${h}h ${r}m`;
  return `${r}m`;
};

const DeskTracker = ({ authHeaders, basePath = '/admin', backHref = null, onLogout = null }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // "leave" flow state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [category, setCategory] = useState('prayer');
  const [reason, setReason] = useState('');
  // "return" flow state
  const [outcomePickerOpen, setOutcomePickerOpen] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [failFormOpen, setFailFormOpen] = useState(false);
  // Live-elapsed timer while an open row exists.
  const [elapsed, setElapsed] = useState(0);
  const tickRef = useRef(null);

  // Feb 2026 — Centralised session-expiry handler. Any 401 from a
  // desk endpoint should sign the user out and bounce them to the
  // right login page. Falls back to the global interceptor when the
  // adapter didn't wire an `onLogout` prop (defensive default).
  const handleSessionExpired = useCallback((err) => {
    if (err?.response?.status !== 401) return false;
    toast.error('Session expired — please sign in again.');
    if (typeof onLogout === 'function') {
      onLogout();
    }
    return true;
  }, [onLogout]);

  // Manual sign-out button in the header. Confirms first so a stray
  // tap doesn't kill a live "away from desk" timer.
  const doManualSignOut = useCallback(() => {
    if (busy) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm('Sign out of Desk Tracker?')
      : true;
    if (!ok) return;
    if (typeof onLogout === 'function') {
      onLogout();
    } else {
      // Defensive fallback — no adapter wired us up. Send the user
      // to the admin login (the most common entry).
      navigate('/admin/login', { replace: true });
    }
  }, [busy, onLogout, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${basePath}/desk/current`, { headers: authHeaders });
      setState(data);
    } catch (err) {
      if (handleSessionExpired(err)) return;
      toast.error(err?.response?.data?.detail || 'Failed to load desk state');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, basePath, handleSessionExpired]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (!state?.open_row?.out_at) { setElapsed(0); return; }
    const outAt = parseUtcIso(state.open_row.out_at);
    if (!Number.isFinite(outAt)) { setElapsed(0); return; }
    const compute = () => {
      const now = Date.now();
      setElapsed(Math.max(0, Math.floor((now - outAt) / 60000)));
    };
    compute();
    tickRef.current = setInterval(compute, 20000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [state?.open_row?.out_at]);

  const openLeavePicker = () => {
    setCategory('prayer');
    setReason('');
    setPickerOpen(true);
  };

  const submitLeave = async () => {
    if (category === 'other' && !reason.trim()) {
      toast.error('Please describe why you are leaving the desk.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}${basePath}/desk/leave`, {
        category, reason: reason.trim(),
      }, { headers: authHeaders });
      toast.success('Desk marked as left. Timer running.');
      setPickerOpen(false);
      await load();
    } catch (e) {
      if (handleSessionExpired(e)) return;
      toast.error(e?.response?.data?.detail || 'Failed to leave desk');
    } finally {
      setBusy(false);
    }
  };

  const submitReturn = async (outcome, extraReason = '') => {
    setBusy(true);
    try {
      const body = { outcome };
      if (outcome === 'unsuccess') body.failure_reason = extraReason.trim();
      const { data } = await axios.post(`${API}${basePath}/desk/return`, body, { headers: authHeaders });
      toast.success(`Welcome back! Away ${data.duration_display}.`);
      setOutcomePickerOpen(false);
      setFailFormOpen(false);
      setFailReason('');
      await load();
    } catch (e) {
      if (handleSessionExpired(e)) return;
      toast.error(e?.response?.data?.detail || 'Failed to return to desk');
    } finally {
      setBusy(false);
    }
  };

  const submitUnsuccess = () => {
    if (!failReason.trim()) {
      toast.error('Please describe why the task was unsuccessful.');
      return;
    }
    submitReturn('unsuccess', failReason);
  };

  const open = state?.open_row;
  const isOut = !!open;

  const onBack = () => {
    if (backHref) navigate(backHref);
    else if (window.history.length > 1) navigate(-1);
    else navigate(basePath === '/branch' ? '/branch/my-work' : '/admin/my-work');
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="inline-flex items-center gap-2 text-slate-500" data-testid="desk-tracker-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading desk state…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-neutral-50 flex flex-col" data-testid="desk-tracker-page">
      {/* Header */}
      <header className="px-4 pt-4 pb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center w-10 h-10 rounded-full text-neutral-600 hover:bg-neutral-200 active:bg-neutral-300 transition"
          aria-label="Back"
          data-testid="desk-tracker-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-neutral-900 leading-tight" data-testid="desk-tracker-title">
            Desk Tracker
          </h1>
          <p className="text-xs text-neutral-500 leading-tight">Quick log — no duty hours needed</p>
        </div>
        <button
          type="button"
          onClick={doManualSignOut}
          disabled={busy}
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-full border border-red-200 bg-white text-red-600 text-sm font-semibold hover:bg-red-50 active:bg-red-100 disabled:opacity-60 transition"
          aria-label="Sign out of Desk Tracker"
          data-testid="desk-tracker-signout"
        >
          <LogOut className="w-4 h-4" strokeWidth={2.5} />
          <span className="hidden xs:inline sm:inline">Sign Out</span>
        </button>
      </header>

      {/* Status card */}
      <div className="px-4 pt-2">
        <div
          className={`rounded-2xl border p-4 ${isOut ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}
          data-testid="desk-tracker-status"
        >
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${isOut ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}
              aria-hidden="true"
            />
            <span className={`text-sm font-semibold ${isOut ? 'text-amber-800' : 'text-emerald-800'}`}>
              {isOut ? 'Away from desk' : 'At desk'}
            </span>
          </div>
          {isOut ? (
            <div className="mt-3">
              <div className="text-xs text-amber-700 uppercase tracking-wide">Reason</div>
              <div className="text-base font-semibold text-neutral-900 mt-0.5" data-testid="desk-tracker-current-category">
                {CATEGORY_LABEL[open.category] || open.category}
                {open.reason ? <span className="font-normal text-neutral-600"> · {open.reason}</span> : null}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-bold tabular-nums text-neutral-900" data-testid="desk-tracker-elapsed">
                  {_fmtElapsed(elapsed)}
                </span>
                <span className="text-xs text-neutral-500">elapsed</span>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-sm text-neutral-700">
              Ready to step away? Tap the button below to log your reason.
            </p>
          )}
        </div>
      </div>

      {/* Primary action zone */}
      <div className="flex-1 px-4 pt-6 pb-6 flex flex-col justify-center gap-4">
        {isOut ? (
          <>
            <button
              type="button"
              onClick={() => setOutcomePickerOpen(true)}
              disabled={busy}
              className="w-full min-h-[92px] rounded-3xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60 text-white font-bold text-2xl shadow-lg flex items-center justify-center gap-3 transition"
              data-testid="desk-tracker-return-btn"
            >
              <LogIn className="w-8 h-8" strokeWidth={2.5} />
              Return to Desk
            </button>
            <p className="text-center text-xs text-neutral-500">
              Confirm outcome after tapping
            </p>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={openLeavePicker}
              disabled={busy}
              className="w-full min-h-[92px] rounded-3xl bg-amber-600 hover:bg-amber-700 active:bg-amber-800 disabled:opacity-60 text-white font-bold text-2xl shadow-lg flex items-center justify-center gap-3 transition"
              data-testid="desk-tracker-leave-btn"
            >
              <LogOut className="w-8 h-8" strokeWidth={2.5} />
              Leave Desk
            </button>
            <p className="text-center text-xs text-neutral-500">
              Log an absence — available 24/7
            </p>
          </>
        )}
      </div>

      {/* Leave picker */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6"
          style={{ background: 'rgba(15,23,42,0.60)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setPickerOpen(false); }}
          data-testid="desk-tracker-leave-picker"
        >
          <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 pb-6 shadow-2xl">
            <div className="w-10 h-1.5 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" aria-hidden="true" />
            <h2 className="text-lg font-bold text-neutral-900 mb-1">Why are you leaving?</h2>
            <p className="text-xs text-neutral-500 mb-4">Tap a reason. This is required.</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {CATEGORY_OPTIONS.map(({ key, label, Icon }) => {
                const active = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCategory(key)}
                    className={`min-h-[64px] rounded-xl border-2 px-3 py-2 flex flex-col items-center justify-center gap-1 transition ${
                      active
                        ? 'border-amber-500 bg-amber-50 text-amber-800'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
                    }`}
                    data-testid={`desk-tracker-cat-${key}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-semibold">{label}</span>
                  </button>
                );
              })}
            </div>
            {category === 'other' && (
              <div className="mb-4">
                <label className="text-xs font-semibold text-neutral-700 mb-1 block">
                  Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Briefly describe…"
                  className="w-full h-12 px-3 rounded-xl border border-neutral-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 text-base"
                  data-testid="desk-tracker-other-reason"
                  autoFocus
                />
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                disabled={busy}
                className="flex-1 h-12 rounded-xl border border-neutral-300 bg-white text-neutral-700 font-semibold hover:bg-neutral-50 disabled:opacity-60 transition"
                data-testid="desk-tracker-leave-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitLeave}
                disabled={busy}
                className="flex-[2] h-12 rounded-xl bg-amber-600 hover:bg-amber-700 active:bg-amber-800 disabled:opacity-60 text-white font-bold inline-flex items-center justify-center gap-2 transition"
                data-testid="desk-tracker-leave-confirm"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
                Confirm leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return outcome picker */}
      {outcomePickerOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6"
          style={{ background: 'rgba(15,23,42,0.60)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !busy) { setOutcomePickerOpen(false); setFailFormOpen(false); } }}
          data-testid="desk-tracker-outcome-picker"
        >
          <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 pb-6 shadow-2xl">
            <div className="w-10 h-1.5 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" aria-hidden="true" />
            {!failFormOpen ? (
              <>
                <h2 className="text-lg font-bold text-neutral-900 mb-1">How did it go?</h2>
                <p className="text-xs text-neutral-500 mb-4">Pick an outcome to close this desk record.</p>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => submitReturn('success')}
                    disabled={busy}
                    className="w-full min-h-[72px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60 text-white font-bold text-lg flex items-center justify-center gap-3 shadow-md transition"
                    data-testid="desk-tracker-outcome-success"
                  >
                    <CheckCircle2 className="w-7 h-7" strokeWidth={2.5} />
                    Success
                  </button>
                  <button
                    type="button"
                    onClick={() => setFailFormOpen(true)}
                    disabled={busy}
                    className="w-full min-h-[72px] rounded-2xl bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white font-bold text-lg flex items-center justify-center gap-3 shadow-md transition"
                    data-testid="desk-tracker-outcome-unsuccess"
                  >
                    <XCircle className="w-7 h-7" strokeWidth={2.5} />
                    Unsuccess
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setOutcomePickerOpen(false)}
                  disabled={busy}
                  className="w-full mt-4 h-12 rounded-xl border border-neutral-300 bg-white text-neutral-700 font-semibold hover:bg-neutral-50 disabled:opacity-60 transition"
                  data-testid="desk-tracker-outcome-cancel"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-neutral-900 mb-1">What went wrong?</h2>
                <p className="text-xs text-neutral-500 mb-4">This will be logged for follow-up.</p>
                <textarea
                  value={failReason}
                  onChange={(e) => setFailReason(e.target.value)}
                  placeholder="Describe the issue…"
                  rows={4}
                  className="w-full px-3 py-3 rounded-xl border border-neutral-300 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 text-base mb-4"
                  data-testid="desk-tracker-fail-reason"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setFailFormOpen(false); setFailReason(''); }}
                    disabled={busy}
                    className="flex-1 h-12 rounded-xl border border-neutral-300 bg-white text-neutral-700 font-semibold hover:bg-neutral-50 disabled:opacity-60 transition"
                    data-testid="desk-tracker-fail-back"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={submitUnsuccess}
                    disabled={busy}
                    className="flex-[2] h-12 rounded-xl bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white font-bold inline-flex items-center justify-center gap-2 transition"
                    data-testid="desk-tracker-fail-submit"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />}
                    Submit unsuccess
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeskTracker;
